import React from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { FileBarChart, Building2, ShoppingCart, Wallet, Users, ShieldCheck, Siren, Lock, ArrowRight, BarChart3, CalendarClock, MapPin, Wand2 } from 'lucide-react';
import WorkOrderMISReport from './reports/WorkOrderMISReport';
import DailyAttendanceReport from './reports/DailyAttendanceReport';
import OtherLocationHistory from './reports/OtherLocationHistory';
import { useAuth } from '../contexts/AuthContext';

// Settings_Insights_Audit_Fix Item 3 — each static report name mapped to its
// closest Custom Report Builder domain (from GET /reports/builder/domains).
// Reports with no clean match (GST/TDS, HSE, Quality — no matching domain in
// the builder's DOMAIN_REGISTRY) are left unmapped and shown as "not yet
// available" rather than force a bad mapping.
const REPORT_DOMAIN_MAP = {
  'Project Progress Summary': 'projects',
  'Project Cost Report': 'projects',
  'S-Curve Report': 'dpr',
  'Milestone Status': 'projects',
  'Resource Utilisation': 'labour',
  'PO Summary': 'procurement_po',
  'Vendor Performance': 'procurement_po',
  'Pending POs': 'procurement_po',
  'Material Consumption': 'stores_inventory',
  'Stock Position': 'stores_inventory',
  'Project P&L': 'finance_client_bills',
  'Cash Flow': 'finance_client_bills',
  'AR Aging': 'finance_client_bills',
  'Manpower On Roll': 'hr_employees',
  'Payroll Register': 'hr_payroll',
  'Labour Cost': 'labour',
};

const STATIC_REPORTS = [
  { cat: 'Project', icon: Building2, items: ['Project Progress Summary','Project Cost Report','S-Curve Report','Milestone Status','Resource Utilisation'] },
  { cat: 'Procurement', icon: ShoppingCart, items: ['PO Summary','Vendor Performance','Pending POs','Material Consumption','Stock Position'] },
  { cat: 'Finance', icon: Wallet, items: ['Project P&L','Cash Flow','AP Aging','AR Aging','Budget Variance','GST Report','TDS Report'] },
  { cat: 'HR', icon: Users, items: ['Manpower On Roll','Payroll Register','Labour Cost'] },
  { cat: 'HSE', icon: Siren, items: ['Monthly HSE Performance','Incident Summary','Training Compliance'] },
  { cat: 'Quality', icon: ShieldCheck, items: ['NCR Status','Snag List','Test Result Summary'] },
];

function ReportsIndex() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isExec = role === 'super_admin' || role === 'md' || role === 'ceo' || role === 'cmd' || ['MD', 'CEO', 'CMD'].includes(dept);
  const canViewAttendance = isExec || role === 'cfo' || role === 'hr' ||
    ['CFO', 'HR'].includes(dept) || role === 'admin' ||
    ['ADMIN', 'ADMINISTRATION'].includes(dept);

  return (
    <div>
      <PageHeader overline="Reports & MIS" title="Business Intelligence" subtitle="Pre-built reports across all modules — exportable to Excel and PDF." />

      {/* Live / wired reports — actually downloadable */}
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-emerald-700 mb-2">⚡ Live Reports</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/app/reports/work-orders-mis"
            data-testid="report-work-orders-mis"
            className={`bg-white border-2 rounded-sm p-5 transition-all relative ${isExec ? 'border-[#3b4872] hover:shadow-lg cursor-pointer' : 'border-slate-200 opacity-60 cursor-not-allowed'}`}
            onClick={(e) => { if (!isExec) e.preventDefault(); }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-sm bg-[#3b4872] text-white flex items-center justify-center">
                <BarChart3 size={17} />
              </div>
              {!isExec && (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-700 font-display font-bold">
                  <Lock size={11} /> Exec only
                </span>
              )}
            </div>
            <div className="kpi-overline">Confidential</div>
            <h3 className="font-display font-bold text-base mt-1">Work Order MIS Report</h3>
            <p className="text-xs text-slate-600 mt-1">FY-wise WO register · Summary + per-FY sheets · One-click Excel · MD / CEO / CMD only.</p>
            {isExec && (
              <div className="mt-3 text-xs text-[#3b4872] font-semibold flex items-center gap-1">
                Open report <ArrowRight size={12} />
              </div>
            )}
          </Link>

          <Link
            to="/app/reports/daily-attendance"
            data-testid="report-daily-attendance"
            className={`bg-white border-2 rounded-sm p-5 transition-all relative ${canViewAttendance ? 'border-[#3b4872] hover:shadow-lg cursor-pointer' : 'border-slate-200 opacity-60 cursor-not-allowed'}`}
            onClick={(e) => { if (!canViewAttendance) e.preventDefault(); }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-sm bg-emerald-600 text-white flex items-center justify-center">
                <CalendarClock size={17} />
              </div>
              {!canViewAttendance && (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-700 font-display font-bold">
                  <Lock size={11} /> Restricted
                </span>
              )}
            </div>
            <div className="kpi-overline">Auto · 20:00 IST</div>
            <h3 className="font-display font-bold text-base mt-1">Daily Attendance Report</h3>
            <p className="text-xs text-slate-600 mt-1">Project-wise headcount snapshot · Auto-emailed to MD / CEO / CMD / CFO / HR / Super Admin daily.</p>
            {canViewAttendance && (
              <div className="mt-3 text-xs text-[#3b4872] font-semibold flex items-center gap-1">
                Open report <ArrowRight size={12} />
              </div>
            )}
          </Link>

          <Link
            to="/app/reports/other-location-history"
            data-testid="report-other-location-history"
            className={`bg-white border-2 rounded-sm p-5 transition-all relative ${canViewAttendance ? 'border-amber-400 hover:shadow-lg cursor-pointer' : 'border-slate-200 opacity-60 cursor-not-allowed'}`}
            onClick={(e) => { if (!canViewAttendance) e.preventDefault(); }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-sm bg-amber-500 text-white flex items-center justify-center">
                <MapPin size={17} />
              </div>
              {!canViewAttendance && (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-700 font-display font-bold">
                  <Lock size={11} /> Restricted
                </span>
              )}
            </div>
            <div className="kpi-overline">HR · Attendance</div>
            <h3 className="font-display font-bold text-base mt-1">Other Location Punch History</h3>
            <p className="text-xs text-slate-600 mt-1">Full history of "Other location" attendance punches — Pending, Approved &amp; Rejected — with who acted and when.</p>
            {canViewAttendance && (
              <div className="mt-3 text-xs text-amber-700 font-semibold flex items-center gap-1">
                Open report <ArrowRight size={12} />
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Static catalogue — most now link into the Custom Report Builder */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Report Catalogue</div>
          <Link to="/app/reports/builder" data-testid="report-open-builder" className="text-[11px] font-display font-bold text-[#3b4872] flex items-center gap-1 hover:underline">
            <Wand2 size={12} /> Open Custom Report Builder
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STATIC_REPORTS.map((r) => (
            <div key={r.cat} className="bg-white border border-slate-200 rounded-sm">
              <div className="p-5 border-b border-slate-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-indigo-50 text-[#3b4872] flex items-center justify-center">
                  <r.icon size={17} />
                </div>
                <div>
                  <div className="kpi-overline">{r.cat}</div>
                  <h3 className="font-display font-bold text-base mt-0.5">{r.cat} Reports</h3>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {r.items.map(i => {
                  const domain = REPORT_DOMAIN_MAP[i];
                  if (!domain) {
                    return (
                      <div
                        key={i}
                        data-testid={`report-${i.toLowerCase().replace(/\s+/g,'-')}`}
                        title="No matching Report Builder domain yet"
                        className="w-full text-left px-5 py-3 flex items-center justify-between text-sm text-slate-400 cursor-not-allowed"
                      >
                        <span className="flex items-center gap-2"><FileBarChart size={13} className="text-slate-300" />{i}</span>
                        <span className="text-[9px] uppercase tracking-widest font-display font-bold">Not yet available</span>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={i}
                      data-testid={`report-${i.toLowerCase().replace(/\s+/g,'-')}`}
                      onClick={() => navigate(`/app/reports/builder?domain=${domain}`)}
                      title={`Build this in the Custom Report Builder (${domain})`}
                      className="w-full text-left px-5 py-3 hover:bg-indigo-50/40 flex items-center justify-between text-sm group"
                    >
                      <span className="flex items-center gap-2 text-slate-700"><FileBarChart size={13} className="text-slate-400" />{i}</span>
                      <Wand2 size={13} className="text-slate-400 group-hover:text-[#3b4872]" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <Routes>
      <Route index element={<ReportsIndex />} />
      <Route path="work-orders-mis" element={<WorkOrderMISReport />} />
      <Route path="daily-attendance" element={<DailyAttendanceReport />} />
      <Route path="other-location-history" element={<OtherLocationHistory />} />
      <Route path="progress" element={<Navigate to="/app/dpr/progress" replace />} />
    </Routes>
  );
}
